import { useOutletContext } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

import { useTags } from 'lib/hooks/queries/tags/useTags';
import { useForms } from 'lib/hooks/queries/forms/useForms';
import { useServices } from 'lib/hooks/queries/services/useServices';
import { useFormPackets } from 'lib/hooks/queries/forms/useFormPackets';
import { usePractitioners } from 'lib/hooks/queries/practitioners/usePractitioners';
import { usePacketBuilderContext } from 'lib/context/PacketBuilder/PacketBuilderContext';
import { PacketBuilderContextProvider } from 'lib/context/PacketBuilder/PacketBuilderContextProvider';

import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';
import WizardModal from 'components/shared/Modal/WizardModal/WizardModal';
import ErrorBoundary, { withErrorBoundary } from 'components/shared/Error/Boundary';
import STEP_DEFINITIONS from 'components/shared/OnboardingWizard/lib/stepDefinitions';
import SUBSTEP_DEFINITIONS from 'components/shared/OnboardingWizard/lib/substepDefinitions';

import ViewForm from '../FormBuilder/components/ViewForm';

import Tags from './components/Tags';
import Forms from './components/Forms';
import Services from './components/Services';
import Providers from './components/Providers';
import EmptyPackets from './components/EmptyPackets';
import FooterButtons from './components/FooterButtons';
import MotionActions from './components/MotionActions';
import { DEFAULT_FILTERS, defaultColumns } from './lib/helpers';
import NameAndAutomation from './components/NameAndAutomation/NameAndAutomation';

const OnboardingPackets = ({
  setHeaderButtonProps,
  navigateToSubstep,
  addFooterActions,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  useEffect(() => {
    addFooterActions([
      {
        text: 'Continue',
        onClick: () => mutateOrchestratorData({}),
        isDisabled: orchestratorLoading
      }
    ]);
  }, [setHeaderButtonProps, addFooterActions]);

  return (
    <PacketBuilderContextProvider>
      <PacketBuilderProcess
        navigateToSubstep={navigateToSubstep}
        setHeaderButtonProps={setHeaderButtonProps}
      />
    </PacketBuilderContextProvider>
  );
};

const PacketBuilderProcess = ({ setHeaderButtonProps, navigateToSubstep }) => {
  const kind = 'appointment';
  const { practice } = useOutletContext() || {};

  const {
    openModal,
    setOpenModal,
    autoSave,
    saveButtonText,
    breadCrumbs,
    handleClose,
    handleSave,
    handleAutoSave,
    debouncedSearchTerm,
    isFormSavingLoading,
    formId,
    setIsEdit,
    setBreadCrumbs,
    setPacket,
    formik,
    editInitialBreadcrumbs,
    isEdit
  } = usePacketBuilderContext();

  const [viewForm, setViewForm] = useState(false);
  const [formToView, setFormToView] = useState();

  const { data: providersList } = usePractitioners(
    { searchTerm: debouncedSearchTerm?.providers, practice_id: practice?.id },
    {},
    debouncedSearchTerm?.providers && [debouncedSearchTerm?.providers]
  );

  const { data: formsList } = useForms({
    params: {
      limit: 10000,
      practiceId: practice?.id,
      filters: {
        name: [{ formula: 'contains', values: debouncedSearchTerm?.forms }]
      }
    },
    dependencies: [debouncedSearchTerm?.forms, practice?.id],
    options: { refetchOnMount: 'always' }
  });

  const { data: servicesList } = useServices({
    params: {
      searchTerm: debouncedSearchTerm?.services,
      practiceId: practice?.id
    },
    dependencies: [debouncedSearchTerm?.services, practice?.id]
  });

  const { data: tagsList } = useTags({
    params: { kind, searchTerm: debouncedSearchTerm?.tags, practiceId: practice?.id },
    dependencies: [debouncedSearchTerm?.tags, kind, practice?.id]
  });

  const handleFormView = (item) => {
    setFormToView(item);
    setOpenModal(false);
    setViewForm(true);
  };

  const actionViews = {
    name_and_automation: {
      component: (
        <NameAndAutomation
          currentBreadcrumb="name_and_automation"
          handleFormView={handleFormView}
          handleAddNewForm={() => {
            navigateToSubstep(
              SUBSTEP_DEFINITIONS[STEP_DEFINITIONS.CUSTOM_FORMS.code].CLINICAL_FORMS.code
            );
          }}
        />
      ),
      footer: <FooterButtons />
    },
    providers: {
      component: <Providers currentBreadcrumb="providers" providersList={providersList} />,
      footer: <FooterButtons nextTo={true} backTo={true} showNavigationButtons={true} />
    },
    services: {
      component: <Services currentBreadcrumb="services" servicesList={servicesList} />,
      footer: <FooterButtons nextTo={true} backTo={true} showNavigationButtons={true} />
    },
    tags: {
      component: <Tags currentBreadcrumb="tags" tagsList={tagsList} />,
      footer: <FooterButtons nextTo={true} backTo={true} showNavigationButtons={true} />
    },
    forms: {
      component: (
        <Forms
          currentBreadcrumb="forms"
          formsList={formsList}
          formId={formId}
          handleAddNewForm={() => {
            navigateToSubstep(
              SUBSTEP_DEFINITIONS[STEP_DEFINITIONS.CUSTOM_FORMS.code].CLINICAL_FORMS.code
            );
          }}
        />
      ),
      footer: <FooterButtons nextTo={true} backTo={true} showNavigationButtons={true} isLastStep />
    }
  };

  const getCurrentView = () => {
    const currentView = breadCrumbs?.find((row) => row.active)?.view;
    return actionViews[currentView]?.component;
  };

  const getCurrentFooter = () => {
    const currentView = breadCrumbs?.find((row) => row.active)?.view;
    return actionViews[currentView]?.footer;
  };

  const onCellClicked = async (e) => {
    if (e?.column?.getColId() === 'actions') return;
    if (['actions', '0'].includes(e?.column?.colId)) return;
    setIsEdit(true);
    setBreadCrumbs(editInitialBreadcrumbs);
    setPacket({
      ...e.data
    });
    formik.setValues({
      ...formik.values,
      ...e.data,
      tag_ids: e?.data?.tag_ids?.map((tag) => tag?.value) ?? [],
      provider_ids: e?.data?.provider_ids?.map((provider) => provider?.value) ?? [],
      service_ids: e?.data?.service_ids?.map((service) => service?.value) ?? [],
      form_ids: e?.data?.form_ids?.map((form) => form?.value) ?? []
    });
    setOpenModal(true);
  };

  useEffect(() => {
    setHeaderButtonProps({
      text: 'New Packet',
      onClick: () => setOpenModal(true),
      'data-qa': 'new-packet-btn',
      className: '!h-[30px]',
      textClassName: 'whitespace-nowrap'
    });
  }, []);

  const renderEmptyState = useCallback(() => <EmptyPackets />, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorMessage}>
      <AgTablePage
        defaultColumns={defaultColumns}
        defaultFilters={DEFAULT_FILTERS}
        useData={useFormPackets}
        title="Packet"
        name="packets"
        noHeader
        noRowsOverlayComponent={renderEmptyState}
        onSelectRow={(e) => onCellClicked(e)}
        actions={(props) => (
          <MotionActions
            {...props}
            handleOpen={setOpenModal}
            setMotionHeaderButtonProps={setHeaderButtonProps}
          />
        )}
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ practice_id: practice?.id }}
        autoHeight
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
      />
      {openModal && (
        <WizardModal
          isOpen={openModal}
          handleClose={handleClose}
          handleSaveProgress={handleSave}
          saveProgressLoading={isFormSavingLoading}
          handleAutoSave={handleAutoSave}
          showAutoSave={true}
          showSaveProgress={true}
          autoSave={autoSave}
          saveButtonText={saveButtonText}
          breadcrumbs={breadCrumbs}
          setBreadCrumbs={setBreadCrumbs}
          enableNavigation={isEdit}
          footerButtons={getCurrentFooter()}>
          {getCurrentView()}
        </WizardModal>
      )}
      {viewForm && (
        <ViewForm form={formToView} setViewFormModal={setViewForm} viewFormModal={viewForm} />
      )}
    </ErrorBoundary>
  );
};

export default withErrorBoundary(OnboardingPackets);
